<script setup lang="ts">
// A thread, Slack-style: a top-level message and its replies, in a side panel with its own
// box, like a small channel inside the channel. Open while ?thread=<message id> is in the URL.
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import type { ConversationInfo } from '../api/types'
import { displayName, formatTime, timelineItems } from '../lib/format'
import { annotations } from '../lib/annotations'
import { mayModerate } from '../lib/roles'
import { useSectionNav } from '../lib/sectionNav'
import { useSession } from '../stores/session'
import { useUnread } from '../stores/unread'
import Avatar from './Avatar.vue'
import Composer from './Composer.vue'
import Icon from './Icon.vue'
import MessageRow from './MessageRow.vue'

const props = defineProps<{ conv: ConversationInfo }>()
const session = useSession()
const unread = useUnread()
const route = useRoute()
const nav = useSectionNav('thread')
const scroller = ref<HTMLElement | null>(null)
const highlighted = ref<string | null>(null)

// Wide screens: the panel sits beside the chat, drag its left edge to resize (remembered on
// this device). Narrower, it covers the chat at full width.
const MIN_WIDTH = 320
const WIDTH_KEY = 'hrthThreadWidth'
const wideQuery = globalThis.matchMedia?.('(min-width: 1024px)')
const wide = ref(wideQuery?.matches ?? true)
const onWide = (e: MediaQueryListEvent) => (wide.value = e.matches)
wideQuery?.addEventListener('change', onWide)
onBeforeUnmount(() => wideQuery?.removeEventListener('change', onWide))
const DEFAULT_WIDTH = 416
function storedWidth(): number {
  try {
    return Number(localStorage.getItem(WIDTH_KEY)) || DEFAULT_WIDTH
  } catch {
    return DEFAULT_WIDTH
  }
}
function saveWidth(): void {
  try {
    localStorage.setItem(WIDTH_KEY, String(width.value))
  } catch {
    // private mode: it just isn't remembered
  }
}
const width = ref(storedWidth())
/** At most 60% of the window, so the chat keeps room. */
const clamp = (w: number) => Math.round(Math.max(MIN_WIDTH, Math.min(w, window.innerWidth * 0.6)))
const resizing = ref(false)
function startResize(e: PointerEvent): void {
  const startX = e.clientX
  const startW = clamp(width.value)
  const handle = e.currentTarget as HTMLElement
  handle.setPointerCapture(e.pointerId)
  resizing.value = true
  const move = (ev: PointerEvent) => (width.value = clamp(startW + startX - ev.clientX))
  const up = () => {
    resizing.value = false
    handle.removeEventListener('pointermove', move)
    handle.removeEventListener('pointerup', up)
    handle.removeEventListener('pointercancel', up)
    saveWidth()
  }
  handle.addEventListener('pointermove', move)
  handle.addEventListener('pointerup', up)
  handle.addEventListener('pointercancel', up)
}
/** Arrow keys on the handle resize too. */
function resizeKey(e: KeyboardEvent): void {
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
  e.preventDefault()
  width.value = clamp(width.value + (e.key === 'ArrowLeft' ? 32 : -32))
  saveWidth()
}
/** Double-clicking the handle puts the width back. */
function resetWidth(): void {
  width.value = DEFAULT_WIDTH
  saveWidth()
}

const msgs = computed(() => session.messages[props.conv.id] ?? [])
const byId = computed(() => new Map(msgs.value.map((m) => [m.envelope.id, m])))
const root = computed(() => (nav.at.value ? byId.value.get(nav.at.value) : undefined))
const replies = computed(() => {
  const roots = unread.trees[props.conv.id]?.roots
  const id = root.value?.envelope.id
  return id && roots ? msgs.value.filter((m) => roots.get(m.envelope.id) === id) : []
})
const groups = computed(() => timelineItems(replies.value).filter((i) => i.kind === 'group'))
const space = computed(() => session.spaces.find((s) => s.id === props.conv.space_id) ?? null)
const ann = computed(() =>
  annotations(msgs.value, (actor, author) => !!space.value && mayModerate(space.value, actor, author, 'hide_messages')),
)
const muted = computed(() => !!root.value && unread.mutedThreads.has(root.value.envelope.id))
const rootTs = computed(() => (root.value ? root.value.envelope.ts || root.value.receivedAt : 0))

// Open (and the page visible) is read: new replies too.
function markRead(): void {
  const last = replies.value[replies.value.length - 1]
  if (root.value) unread.markThreadRead(root.value.envelope.id, last?.seq ?? root.value.seq)
}
watch([() => root.value?.envelope.id, () => replies.value.length], markRead, { immediate: true })
document.addEventListener('visibilitychange', markRead)
onBeforeUnmount(() => document.removeEventListener('visibilitychange', markRead))

// Esc returns to the channel, unless it's for something else (a box, a menu).
function onKey(e: KeyboardEvent): void {
  const t = e.target as HTMLElement | null
  if (e.key === 'Escape' && !e.defaultPrevented && !t?.closest('input, textarea, select, [role="dialog"]')) nav.close()
}
window.addEventListener('keydown', onKey)
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))

function nearBottom(): boolean {
  const el = scroller.value
  return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 120
}
async function scrollToBottom(): Promise<void> {
  await nextTick()
  const el = scroller.value
  if (el) el.scrollTop = el.scrollHeight
}
watch(() => root.value?.envelope.id, () => void scrollToBottom(), { immediate: true })
watch(
  () => replies.value.length,
  (n, old) => {
    const last = replies.value[n - 1]
    if (last && n > (old ?? 0) && (nearBottom() || last.sender.user_id === session.me)) void scrollToBottom()
  },
  { flush: 'pre' },
)
// A reply linked from search: ?m=<reply id> next to ?thread=.
watch(
  () => [route.query.m, replies.value.length] as const,
  async ([m]) => {
    if (typeof m !== 'string' || !replies.value.some((r) => r.envelope.id === m)) return
    await nextTick()
    scroller.value?.querySelector(`[data-msg-id="${CSS.escape(m)}"]`)?.scrollIntoView({ block: 'center' })
    highlighted.value = m
    setTimeout(() => (highlighted.value = null), 2500)
  },
  { immediate: true, flush: 'post' },
)
</script>

<template>
  <aside
    class="relative flex shrink-0 flex-col border-l border-border bg-bg-primary"
    :class="resizing ? 'select-none' : ''"
    :style="wide ? { width: `${clamp(width)}px` } : undefined"
    aria-label="Thread"
    data-testid="thread-panel"
  >
    <!-- Wide screens: the left edge drags to resize; a tab on it collapses the panel or is a grip.
         Phones and narrower screens use Back. -->
    <template v-if="wide">
      <div
        class="group/resize absolute inset-y-0 -left-1.5 z-10 w-3 cursor-col-resize focus-visible:outline-none"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize thread"
        :aria-valuenow="clamp(width)"
        tabindex="0"
        data-testid="thread-resize"
        @pointerdown.prevent="startResize"
        @keydown="resizeKey"
        @dblclick="resetWidth"
      >
        <span class="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 transition-colors group-hover/resize:bg-ink group-focus-visible/resize:bg-ink" :class="resizing ? 'bg-ink' : ''" />
      </div>
      <div class="absolute top-1/2 left-0 z-20 flex -translate-x-1/2 -translate-y-1/2 flex-col border border-border bg-bg-secondary text-text-muted">
        <button
          class="flex h-7 w-5 items-center justify-center hover:bg-ink hover:text-paper"
          title="Collapse thread (Esc)"
          aria-label="Collapse thread"
          data-testid="thread-collapse"
          @click="nav.close()"
        >
          <Icon name="right" />
        </button>
        <div
          class="flex h-7 w-5 cursor-col-resize items-center justify-center border-t border-border hover:text-text-primary"
          title="Drag to resize · double-click to reset"
          aria-hidden="true"
          data-testid="thread-grip"
          @pointerdown.prevent="startResize"
          @dblclick="resetWidth"
        >
          <svg viewBox="0 0 6 12" class="h-3 w-1.5" fill="currentColor">
            <circle cx="1.5" cy="2" r="1" /><circle cx="4.5" cy="2" r="1" />
            <circle cx="1.5" cy="6" r="1" /><circle cx="4.5" cy="6" r="1" />
            <circle cx="1.5" cy="10" r="1" /><circle cx="4.5" cy="10" r="1" />
          </svg>
        </div>
      </div>
    </template>
    <p v-if="!root" class="mono p-4 text-text-muted" data-testid="thread-missing">This thread's message isn't on this device.</p>
    <template v-else>
      <!-- The thread's message heads it; Back (or Esc) returns to the channel -->
      <div class="scroll-thin flex max-h-[40%] shrink-0 gap-3 overflow-y-auto border-b border-border px-4 py-3" data-testid="thread-root">
        <Avatar :id="root.sender.user_id" :name="displayName(session.profiles, root.sender.user_id)" :size="32" />
        <div class="min-w-0 flex-1">
          <MessageRow :msg="root" :conv-id="conv.id" :state="ann.get(root.envelope.id)" :thread="root.envelope.id" :reply-to="undefined" :reply-state="undefined">
            <template #head>
              <div class="flex items-baseline gap-3">
                <span class="truncate text-sm font-medium">{{ displayName(session.profiles, root.sender.user_id) }}</span>
                <time class="mono shrink-0 text-text-disabled" :datetime="new Date(rootTs).toISOString()">{{ formatTime(rootTs) }}</time>
              </div>
            </template>
          </MessageRow>
        </div>
        <!-- Beside the message, not in it: its hover actions would cover it -->
        <button
          class="mono w-[6ch] shrink-0 self-start pt-0.5 text-right text-text-disabled uppercase hover:text-text-primary"
          :title="muted ? 'Unmute this thread' : 'Mute this thread: its replies don\'t notify or count as unread on this device'"
          data-testid="thread-panel-mute"
          @click="unread.muteThread(root.envelope.id, !muted)"
        >
          {{ muted ? 'Unmute' : 'Mute' }}
        </button>
      </div>
      <div ref="scroller" class="scroll-thin min-h-0 flex-1 overflow-y-auto px-4 py-4">
        <template v-for="g in groups" :key="g.kind === 'group' ? g.messages[0]!.seq : 0">
          <div v-if="g.kind === 'group'" class="mt-4 flex gap-3 first:mt-0" data-testid="message-group">
            <Avatar :id="g.sender" :name="displayName(session.profiles, g.sender)" :size="32" />
            <div class="min-w-0 flex-1">
              <MessageRow
                v-for="(m, i) in g.messages"
                :key="m.seq"
                :class="highlighted === m.envelope.id ? 'bg-bg-hover ring-1 ring-ink' : ''"
                :msg="m"
                :conv-id="conv.id"
                :state="ann.get(m.envelope.id)"
                :thread="root.envelope.id"
                :reply-to="m.envelope.type === 'text' && m.envelope.reply_to ? byId.get(m.envelope.reply_to) : undefined"
                :reply-state="m.envelope.type === 'text' && m.envelope.reply_to ? ann.get(m.envelope.reply_to) : undefined"
              >
                <template v-if="i === 0" #head>
                  <div class="flex items-baseline gap-3">
                    <span class="text-sm font-medium" data-testid="message-author">{{ displayName(session.profiles, g.sender) }}</span>
                    <time class="mono text-text-disabled" :datetime="new Date(g.firstTs).toISOString()">{{ formatTime(g.firstTs) }}</time>
                  </div>
                </template>
              </MessageRow>
            </div>
          </div>
        </template>
      </div>
      <Composer :conv="conv" :thread="root" placeholder="Reply…" />
    </template>
  </aside>
</template>
